// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@synd/MobiusOverrides/Button/Button";
import { getSystemColor, xParserOptions } from "@synd/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";

const enum fields {
    textColor = "textColor",
    backgroundColor = "backgroundColor",
    heading = "heading",
    description = "description",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.textColor]: string;
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.description]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.openInNewWindow]: boolean;
    };
}

export interface PromoWidgetStyles {
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    divider?: InjectableCss;
    content?: InjectableCss;
    description?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
}

export const promoWidgetStyles: PromoWidgetStyles = {};

export const PromoWidget: FC<OwnProps> = ({
    fields: { buttonLabel, buttonLink, heading, description, openInNewWindow, textColor, backgroundColor },
}) => {
    const { url, title } = useGetLink(buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            openInNewWindow ? window.open(url) : (window.location.href = url);
        } else {
            openInNewWindow ? window.open(url) : history.push(url);
        }
    };

    const styles = promoWidgetStyles;

    return (
        <StyledWrapper {...styles.wrapper} {...{ backgroundColor }}>
            {heading && <Typography {...styles.heading} color={textColor}>{parse(heading, xParserOptions)}</Typography>}
            <StyledWrapper {...styles.divider} {...{ textColor }} />
            <StyledWrapper {...styles.content}>
                {description && <Typography {...styles.description} color={textColor}>{parse(description, xParserOptions)}</Typography>}
                {(buttonLabel || title || url) && (
                    <Button {...styles.button} color={textColor} {...{ textColor }} onClick={onClick} aria-label={buttonLabel || title || url}>
                        {buttonLabel || title || url}
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const promoWidget: WidgetModule = {
    component: PromoWidget,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.textColor,
                displayName: "Text color",
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
                fieldType: "General",
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: getSystemColor("common.ui7"),
                fieldType: "General",
            },
            {
                fieldType: "Contextual",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Title",
                defaultValue: "Title",
                extendedConfig: { height: 100 },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.description,
                editorTemplate: "RichTextField",
                displayName: "Description",
                defaultValue: "Description",
                extendedConfig: { height: 170 },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "Label",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLink,
                displayName: "Button URL",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.openInNewWindow,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
        ],
    },
};

export default promoWidget;
